/**
 * Regras de leitura das LIGAÇÕES entre título e pagamento.
 *
 * PEDIDO DO USUÁRIO (09/10/2026): "que seja possível achar e filtrar os
 * documentos linkados, seja clicando nos '...' vertical na linha do documento
 * em conta analítica, ou seja por outro filtro em outro lugar".
 *
 * O backend grava, em cada título, os NÚMEROS dos lançamentos que o baixaram
 * (`ligacoes`) e quanto já foi pago (`valor_baixado`). Medido na 856 em
 * 09/10/2026: 815 títulos com ligação, somando R$ 7.070.314,54 — e um título
 * chega a ter 9 pagamentos (NF 22459, conta 177).
 *
 * Tudo aqui é função pura: a tela só escolhe cor e posição.
 */

import { IComposicaoItem } from './papelTrabalho';

/** Quantos pagamentos este título tem ligados. */
export const qtdLigacoes = (i: { ligacoes?: number[] | null }): number =>
  Array.isArray(i.ligacoes) ? i.ligacoes.length : 0;

/**
 * `true` quando o título tem pagamento identificado — e portanto o menu "..."
 * tem o que abrir.
 *
 * A régua é a LIGAÇÃO, não o `valor_baixado`: um título pode ter sido baixado
 * por um lançamento que o motor não conseguiu identificar, e nesse caso abrir
 * um painel vazio seria pior que não oferecer a ação.
 */
export const temLigacao = (i: { ligacoes?: number[] | null }): boolean => qtdLigacoes(i) > 0;

/**
 * Quanto ainda falta pagar, segundo os números do próprio item.
 *
 * Deriva de `valor_original - valor_baixado` em vez de usar `valor_aberto`
 * porque os dois respondem perguntas diferentes quando há baixa não
 * identificada: `valor_aberto` é o que o motor não conseguiu casar, e este é o
 * que não foi pago. Em geral coincidem.
 */
export const restaPagar = (i: {
  valor_original?: number | null;
  valor_baixado?: number | null;
}): number => {
  const orig = Number(i.valor_original ?? 0);
  const pago = Number(i.valor_baixado ?? 0);
  return Math.round((orig - pago) * 100) / 100;
};

/**
 * Quanto do título já foi pago, em %.
 *
 * `null` quando não há valor original: 0% afirmaria "nada foi pago" onde o
 * correto é "não há o que medir".
 */
export const pctPago = (i: {
  valor_original?: number | null;
  valor_baixado?: number | null;
}): number | null => {
  const orig = Number(i.valor_original ?? 0);
  if (!Number.isFinite(orig) || orig <= 0) return null;
  return Math.round((Number(i.valor_baixado ?? 0) / orig) * 1000) / 10;
};

/**
 * Soma dos lançamentos exibidos.
 *
 * Tipada pelo que USA (`valor`), não por uma interface inteira: serve tanto à
 * linha do razão quanto ao lançamento buscado por número, que têm formatos
 * diferentes e a mesma pergunta.
 */
export const somaLigados = (ls: { valor?: number | null }[]): number =>
  Math.round(ls.reduce((s, l) => s + Number(l.valor ?? 0), 0) * 100) / 100;

/**
 * Identificação curta do título, para o cabeçalho do painel.
 *
 * Prefere a nota fiscal (o que o contador procura no arquivo físico), cai para
 * o documento e por fim para o número do lançamento.
 */
export const tituloDoItem = (i: IComposicaoItem): string => {
  if (i.nota_numero) return `NF ${i.nota_numero}`;
  if (i.documento) return `Doc. ${i.documento}`;
  if (i.numero != null) return `Lanç. ${i.numero}`;
  return 'Título';
};

/**
 * `true` quando vale oferecer o filtro "só títulos com pagamento ligado".
 *
 * Com nenhum item ligado o filtro devolveria lista vazia sempre; com TODOS
 * ligados ele não filtra nada. Nos dois casos ele é ruído.
 */
export const valeFiltrarLigados = (itens: IComposicaoItem[] | undefined): boolean => {
  const ls = itens ?? [];
  if (ls.length < 2) return false;
  const n = ls.filter(temLigacao).length;
  return n > 0 && n < ls.length;
};
